import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  CardActions,
  Stack,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode2 as QRCodeIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { useUserContext } from "../context/UserContext";
import {
  listAllQREvents,
  updateQREvent,
  deleteQREvent,
  listClassrooms,
} from "../context/Api";
import QRCodeGenerator from "../components/QRCodeGenerator";
import PageNavBar from "../components/PageNavBar";
import TimePicker15Min from "../components/TimePicker15Min";
import { useNavigate } from "react-router-dom";

const EventManagement = () => {
  const { userRole, tenantId, selectedTenantId } = useUserContext();
  const navigate = useNavigate();
  const isAdmin = userRole === "admin" || userRole === "SuperAdmin";
  const effectiveTenantId = userRole === "SuperAdmin" ? selectedTenantId : tenantId;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [classrooms, setClassrooms] = useState([]);

  // Form state
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radius, setRadius] = useState("100");
  const [classroomId, setClassroomId] = useState("");

  useEffect(() => {
    if (!isAdmin) {
      setError("Access denied. Only admins can manage events.");
      setLoading(false);
      return;
    }

    if (!effectiveTenantId) {
      if (userRole === "admin") {
        setError("No tenant ID available. Please ensure you are properly associated with a tenant. If this issue persists, contact your system administrator.");
      } else {
        setError("No tenant ID available. Please select a tenant.");
      }
      setLoading(false);
      return;
    }

    fetchEvents();
    fetchClassrooms();
  }, [effectiveTenantId, isAdmin, userRole]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError("");
      const eventList = await listAllQREvents(effectiveTenantId);
      // Sort by date and time (newest first)
      eventList.sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.startTime || "00:00"}`);
        const dateB = new Date(`${b.date}T${b.startTime || "00:00"}`);
        return dateB - dateA;
      });
      setEvents(eventList);
    } catch (err) {
      console.error("Error fetching events:", err);
      setError("Failed to fetch events: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const list = await listClassrooms(effectiveTenantId);
      setClassrooms(list || []);
    } catch (err) {
      console.error("Error fetching classrooms:", err);
    }
  };

  const handleEdit = (event) => {
    setSelectedEvent(event);
    setName(event.name || "");
    setDate(event.date || "");
    setStartTime(event.startTime || "");
    setEndTime(event.endTime || "");
    setLatitude(event.latitude?.toString() || "");
    setLongitude(event.longitude?.toString() || "");
    setRadius(event.radius?.toString() || "100");
    setClassroomId(event.classroomId || "");
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!name || !date || !startTime || !endTime) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setError("");
      const updates = {
        name,
        date,
        startTime,
        endTime,
      };

      if (selectedEvent.type === "event") {
        if (!latitude || !longitude) {
          setError("Please provide latitude and longitude for event location");
          return;
        }
        updates.latitude = Number(latitude);
        updates.longitude = Number(longitude);
        updates.radius = Number(radius) || 100;
      } else if (selectedEvent.type === "classroom") {
        if (!classroomId) {
          setError("Please select a classroom");
          return;
        }
        updates.classroomId = classroomId;
      }

      await updateQREvent(selectedEvent.id, updates);
      setEditDialogOpen(false);
      fetchEvents();
    } catch (err) {
      console.error("Error updating event:", err);
      setError("Failed to update event: " + (err.message || "Unknown error"));
    }
  };

  const handleDelete = async () => {
    try {
      setError("");
      await deleteQREvent(selectedEvent.id);
      setDeleteDialogOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (err) {
      console.error("Error deleting event:", err);
      setError("Failed to delete event: " + (err.message || "Unknown error"));
    }
  };

  const handleViewQR = (event) => {
    setSelectedEvent(event);
    setQrDialogOpen(true);
  };

  const getQRPayload = (event) => {
    const timestamp = Date.now();
    if (event.type === "classroom") {
      if (!event.classroomId) return "";
      return JSON.stringify({ t: "classroom", id: event.classroomId, ts: timestamp });
    }
    return JSON.stringify({ t: "qr_event", id: event.id, ts: timestamp });
  };

  const formatDateTime = (date, time) => {
    if (!date) return "N/A";
    const dateObj = new Date(`${date}T${time || "00:00"}`);
    return dateObj.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isAdmin) {
    return (
      <Box sx={{ minHeight: "100vh", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", py: 4 }}>
        <Container maxWidth="lg">
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Alert severity="error">Access denied. Only admins can manage events.</Alert>
            <Button onClick={() => navigate("/dashboard")} sx={{ mt: 2 }}>
              Go to Dashboard
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        py: 4,
      }}
    >
      <PageNavBar title="Event Management" backTo="/dashboard" />
      <Container maxWidth="xl"
      sx={{ 
        mt: 3, // Solution 1
      }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            background: "rgba(255, 255, 255, 0.98)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <EventIcon sx={{ fontSize: 40, color: "#1976d2", mr: 2 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              Event Management
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              startIcon={<QRCodeIcon />}
              onClick={() => navigate("/qr-event")}
              sx={{ ml: 2 }}
            >
              Create New Event
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : events.length === 0 ? (
            <Card sx={{ textAlign: "center", py: 6 }}>
              <CardContent>
                <EventIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No events found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Create your first event to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<QRCodeIcon />}
                  onClick={() => navigate("/qr-event")}
                >
                  Create Event
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {events.map((event) => (
                <Grid item xs={12} md={6} lg={4} key={event.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {event.name || "Unnamed Event"}
                        </Typography>
                        <Chip
                          label={event.type === "classroom" ? "Classroom" : "Event"}
                          color={event.type === "classroom" ? "primary" : "secondary"}
                          size="small"
                        />
                      </Box>

                      <Stack spacing={1.5} sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <EventIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(event.date, event.startTime)} - {event.endTime || "N/A"}
                          </Typography>
                        </Box>

                        {event.type === "event" && (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <LocationIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              {event.latitude?.toFixed(4)}, {event.longitude?.toFixed(4)}
                            </Typography>
                          </Box>
                        )}

                        {event.type === "classroom" && event.classroomId && (
                          <Typography variant="body2" color="text.secondary">
                            Classroom: {classrooms.find((c) => c.id === event.classroomId)?.name || event.classroomId}
                          </Typography>
                        )}

                        <Chip
                          label={event.active ? "Active" : "Inactive"}
                          color={event.active ? "success" : "default"}
                          size="small"
                          sx={{ width: "fit-content" }}
                        />
                      </Stack>
                    </CardContent>

                    <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewQR(event)}
                        title="View QR Code"
                      >
                        <QRCodeIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(event)}
                        title="Edit Event"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedEvent(event);
                          setDeleteDialogOpen(true);
                        }}
                        title="Delete Event"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Container>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Event Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TimePicker15Min
                label="Start Time"
                value={startTime}
                onChange={setStartTime}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TimePicker15Min
                label="End Time"
                value={endTime}
                onChange={setEndTime}
                fullWidth
              />
            </Grid>
            {selectedEvent?.type === "event" && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Latitude"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Longitude"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Radius (meters)"
                    value={radius}
                    onChange={(e) => setRadius(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </>
            )}
            {selectedEvent?.type === "classroom" && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Classroom</InputLabel>
                  <Select
                    value={classroomId}
                    onChange={(e) => setClassroomId(e.target.value)}
                    label="Classroom"
                  >
                    {classrooms.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name || c.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedEvent?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>QR Code - {selectedEvent?.name}</DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          {selectedEvent && (
            <QRCodeGenerator
              value={getQRPayload(selectedEvent)}
              eventName={selectedEvent.name}
              size={280}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventManagement;

